import React from "react";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { useHubSpotForm } from "@src/hooks/useHubSpotForm";

interface AboutHeroProps {
  settings: {
    section: {
      eyebrow_text?: string;
      eyebrow_text_style?: string;
      eyebrow_text_style_mobile?: string;
      main_heading?: string;
      heading_style?: string;
      heading_style_mobile?: string;
      supporting_copy?: string;
      copy_style?: string;
      copy_style_mobile?: string;
      use_form?: boolean;
      cta_text?: string;
      cta_url?: string;
      section_hubspot_form_id?: string;
      section_hubspot_region?: string;
    };
    mobile_breakpoint?: string;
  };
}

export const AboutHero: React.FC<AboutHeroProps> = ({ settings }) => {
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint || "1024px");
  const section = settings.section;

  const { openForm } = useHubSpotForm({
    portalId: settings.globals.hubspot.portal_id,
    formId: section.section_hubspot_form_id ? section?.section_hubspot_form_id : settings.globals.hubspot.form_id,
    region: section.section_hubspot_region ? section?.section_hubspot_region : settings.globals.hubspot.form_region,
  });

  const handleContactClick = () => {
    openForm();
  };

  return (
    <div
      className={`flex flex-col ${isMobile ? "" : "w-full mx-auto"}`}
    >
      {section.main_heading && (
        <div className="w-full capitalize">
          <Paragraph
            text={section.main_heading}
            type={isMobile ? (section.heading_style_mobile || "h3") : (section.heading_style || "h2")}
          />
        </div>
      )}

      {section.eyebrow_text && (
        <Paragraph
          classes="mt-1 lg:mt-2"
          text={section.eyebrow_text}
          type={isMobile ? (section.eyebrow_text_style_mobile || "about-hero-eyebrow") : (section.eyebrow_text_style || "about-hero-eyebrow")}
        />
      )}

      <div className="w-full flex flex-col lg:flex-row lg:items-start max-lg:pt-[30px]">
        <div className="flex-1" />
        <div className="flex-1 flex flex-col gap-6 lg:mt-[55px] mb-6 lg:mb-[18px]">
          {section.supporting_copy && (
            <Paragraph
              text={section.supporting_copy}
              type={isMobile ? (section.copy_style_mobile || "b_small_light") : (section.copy_style || "b_small_light")}
            />
          )}

          {section.cta_text && (
            <div>
              {section.use_form ? (
                <a href="#" 
                  onClick={handleContactClick} 
                  className="inline-flex justify-center items-center gap-3 pr-6 py-3"
                >
                  <div className="justify-start b_a1">
                    {section.cta_text}
                  </div>
                  <div className="icon-block-wrapper flex items-center">
                    <span className="icon-right-arrow-alt text-[24px] flex-shrink-0" />
                  </div>
                </a>
              ) : (
                <a href={section.cta_url} className="inline-flex justify-center items-center gap-3 pr-6 py-3">
                  <div className="justify-start b_a1">
                    {section.cta_text}
                  </div>
                  <div className="icon-block-wrapper flex items-center">
                    <span className="icon-right-arrow-alt text-[24px] flex-shrink-0" />
                  </div>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <div className=" hidden lg:block absolute right-0 -top-36 w-[105%] h-auto pointer-events-none z-[-1] overflow-visible">
        <svg width="auto" height="auto" viewBox="0 0 1440 2637" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M3183 635.643C3183 1288.03 2659.34 1846.5 2013.5 1846.5C1367.66 1846.5 844 1288.03 844 635.643C844 -16.6985 1367.62 -545.5 2013.5 -545.5C2659.38 -545.5 3183 -16.6984 3183 635.643Z" stroke="url(#paint0_linear_12326_10625)" strokeOpacity="0.15" strokeWidth="3" />
          <path d="M3511 635.643C3511 1288.03 2987.34 1846.5 2341.5 1846.5C1695.66 1846.5 1172 1288.03 1172 635.643C1172 -16.6985 1695.62 -545.5 2341.5 -545.5C2987.38 -545.5 3511 -16.6984 3511 635.643Z" stroke="url(#paint1_linear_12326_10625)" strokeOpacity="0.15" strokeWidth="3" />
          <path d="M583.643 296C1236.03 296 1794.5 819.665 1794.5 1465.5C1794.5 2111.34 1236.03 2635 583.643 2635C-68.6985 2635 -597.5 2111.38 -597.5 1465.5C-597.5 819.617 -68.6985 296 583.643 296Z" stroke="url(#paint3_linear_12326_10625)" strokeOpacity="0.15" strokeWidth="3" />
          <defs>
            <filter id="filter0_f_12326_10625" x="-351.5" y="-213.5" width="1087" height="1087" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="140.25" result="effect1_foregroundBlur_12326_10625" />
            </filter>
            <linearGradient id="paint0_linear_12326_10625" x1="3184.5" y1="665.357" x2="842.5" y2="665.357" gradientUnits="userSpaceOnUse">
              <stop stopColor="#899B30" />
              <stop offset="0.50226" stopColor="#AC7E4C" />
              <stop offset="1" stopColor="#D6CFF8" />
            </linearGradient>
            <linearGradient id="paint1_linear_12326_10625" x1="3512.5" y1="665.357" x2="1170.5" y2="665.357" gradientUnits="userSpaceOnUse">
              <stop stopColor="#899B30" />
              <stop offset="0.50226" stopColor="#AC7E4C" />
              <stop offset="1" stopColor="#D6CFF8" />
            </linearGradient>
            <linearGradient id="paint2_linear_12326_10625" x1="455" y1="332.322" x2="-71" y2="332.363" gradientUnits="userSpaceOnUse">
              <stop stopColor="#917DF1" />
              <stop offset="0.25" stopColor="#D6CFF8" />
              <stop offset="0.5" stopColor="#F1E8DE" />
              <stop offset="0.75" stopColor="#899B30" />
              <stop offset="1" stopColor="#3A4B24" />
            </linearGradient>
            <linearGradient id="paint3_linear_12326_10625" x1="613.357" y1="294.5" x2="613.357" y2="2636.5" gradientUnits="userSpaceOnUse">
              <stop stopColor="#899B30" />
              <stop offset="0.50226" stopColor="#AC7E4C" />
              <stop offset="1" stopColor="#D6CFF8" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="lg:hidden block absolute right-0 -top-36 w-[105%] h-auto pointer-events-none z-[-1]">
        <svg width="300" height="2255" viewBox="0 0 300 2255" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.7" d="M-912.357 -86.0001C-259.967 -86.0001 298.5 437.664 298.5 1083.5C298.5 1729.34 -259.967 2253 -912.357 2253C-1564.7 2253 -2093.5 1729.38 -2093.5 1083.5C-2093.5 437.617 -1564.7 -86 -912.357 -86.0001Z" stroke="url(#paint0_linear_12405_12654)" strokeOpacity="0.15" strokeWidth="3" />
          <defs>
            <linearGradient id="paint0_linear_12405_12654" x1="-882.643" y1="-87.5001" x2="-882.643" y2="2254.5" gradientUnits="userSpaceOnUse">
              <stop stop-color="#899B30" />
              <stop offset="0.50226" stopColor="#AC7E4C" />
              <stop offset="1" stopColor="#D6CFF8" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default AboutHero;
